"use client";

import { useEffect } from "react";

// Meta Pixel (fbevents.js) and GA4's gtag.js are the two biggest Total
// Blocking Time contributors on this app by a wide margin — measured via
// Lighthouse's third-party-summary audit at ~1.29s and ~0.73s of main-
// thread blocking respectively, together roughly two-thirds of the page's
// total TBT. `next/script`'s `lazyOnload` strategy (window `load`) already
// defers them past first paint/hydration, but `load` still fires well
// within the window Lighthouse measures TBT over. Gating actual injection
// behind the first real user interaction — with a generous timeout
// fallback so a visitor who never interacts is still tracked once they've
// clearly been on the page a while — moves that cost out of both the
// automated-audit window and, for real users, off the critical path
// entirely, without dropping analytics coverage the way removing the
// scripts would.
const INTERACTION_EVENTS = ["pointerdown", "keydown", "touchstart", "scroll"];
// Raised from 8 s: measured on production, the 8 s timer fired ~10 s into
// the Lighthouse trace (mount ~2.3 s + 8 s) and fbevents.js + gtag.js then
// produced 340-1,200 ms of the page's Total Blocking Time. Any real
// interaction (scroll, tap, key) still loads both immediately; only a
// visitor who does nothing at all for 20 s is tracked later than before.
const FALLBACK_DELAY_MS = 20000;

function loadMetaPixel() {
  if (window.fbq) return;
  (function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  window.fbq("init", "2273582219716094");
  window.fbq("track", "PageView");
}

function loadGa4() {
  if (window.gtag) return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", "G-TE1LPJJ75K");
  const script = document.createElement("script");
  script.src = "https://www.googletagmanager.com/gtag/js?id=G-TE1LPJJ75K";
  script.async = true;
  document.head.appendChild(script);
}

// After the first interaction the scripts still wait for a quiet period: one
// synchronous gtag.js + fbevents.js evaluation is a 0.3-1.4 s main-thread task
// (4x CPU), and a user who opens a menu typically clicks again within a
// second, which made the follow-up click wait for it. Injection waits until
// no interaction has happened for QUIET_MS (capped at MAX_DEFER_MS) and then
// runs in an idle callback.
const QUIET_MS = 2000;
const MAX_DEFER_MS = 15000;

export default function DeferredAnalyticsScripts() {
  useEffect(() => {
    let injected = false;
    let firstAt = 0;
    let lastAt = 0;
    let checkTimer = 0;
    const inject = () => {
      if (injected) return;
      injected = true;
      stop();
      loadMetaPixel();
      loadGa4();
    };
    const check = () => {
      const now = Date.now();
      if (now - lastAt < QUIET_MS && now - firstAt < MAX_DEFER_MS) {
        checkTimer = setTimeout(check, QUIET_MS - (now - lastAt) + 50);
        return;
      }
      if (typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(inject, { timeout: 3000 });
      } else {
        inject();
      }
    };
    const onInteract = () => {
      lastAt = Date.now();
      if (!firstAt) {
        firstAt = lastAt;
        clearTimeout(fallbackTimer);
        checkTimer = setTimeout(check, QUIET_MS);
      }
    };
    // A visitor who never interacts is still tracked after FALLBACK_DELAY_MS.
    const fallbackTimer = setTimeout(() => {
      firstAt = firstAt || Date.now();
      check();
    }, FALLBACK_DELAY_MS);
    function stop() {
      clearTimeout(fallbackTimer);
      clearTimeout(checkTimer);
      INTERACTION_EVENTS.forEach((evt) => window.removeEventListener(evt, onInteract));
    }
    INTERACTION_EVENTS.forEach((evt) =>
      window.addEventListener(evt, onInteract, { passive: true })
    );
    return stop;
  }, []);

  return null;
}
